import { Injectable, inject, OnDestroy, DOCUMENT } from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';
import { filter, interval, Subject, takeUntil } from 'rxjs';
import { TokenService } from './token.service';
import { AuthService } from './auth.service';
import { RateLimitService } from '../common/rate-limit.service';
import { isSupportPage } from '../support/support-paths';
import { CompanyTerminationService } from '../access/company-termination.service';
import { companyTerminatedDetails } from '../../../shared/utils/company-access.utils';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private readonly tokenService = inject(TokenService);
  private readonly authService = inject(AuthService);
  private readonly rateLimit = inject(RateLimitService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly termination = inject(CompanyTerminationService);
  private readonly destroy$ = new Subject<void>();
  private retryTimer?: ReturnType<typeof setTimeout>;

  // Intervalo de verificación: cada 1 minuto
  private readonly CHECK_INTERVAL = 60 * 1000;
  private isRefreshing = false;

  /**
   * Inicializa el servicio de renovación automática de tokens
   * Debe llamarse al iniciar la aplicación
   */
  startTokenRefreshMonitoring(): void {
    // Monitorear cada minuto si el token está por expirar
    interval(this.CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkAndRefreshToken();
      });

    // También verificar en cada cambio de ruta
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.checkAndRefreshToken();
      });

    // Verificación inicial
    this.checkAndRefreshToken();
  }

  /**
   * Limpia las suscripciones cuando el servicio se destruye
   */
  ngOnDestroy(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verifica si el token está próximo a expirar y lo renueva si es necesario
   */
  private checkAndRefreshToken(): void {
    // Páginas de soporte (/support/start, /support/ended): la pestaña arma o
    // cierra una sesión de soporte y no renueva la sesión normal de las otras
    // pestañas (un refresh en vuelo podría terminar guardado en la sesión de
    // soporte). En modo soporte, además, no hay refresh token.
    if (isSupportPage(this.document.location.pathname)) {
      return;
    }

    const accessToken = this.tokenService.getAccessToken();
    const refreshToken = this.tokenService.getRefreshToken();

    // Si no hay tokens, no hacer nada
    if (!accessToken || !refreshToken) {
      return;
    }

    // Si no hay usuario autenticado, no hacer nada
    if (!this.authService.isAuthenticated()) {
      return;
    }

    // Verificar si el refresh token es válido
    if (!this.tokenService.hasValidRefreshToken()) {
      this.tokenService.clearTokens();
      this.authService.logout();
      this.router.navigate(['/login'], {
        queryParams: { reason: 'session_expired' },
      });
      return;
    }

    // Si ya se está refrescando, evitar llamadas duplicadas
    if (this.isRefreshing) {
      return;
    }

    // Verificar si el token está próximo a expirar (menos de 5 minutos)
    if (this.tokenService.isAccessTokenExpiringSoon()) {
      this.refreshToken();
    }
  }

  /**
   * Renueva el access token usando el refresh token
   */
  private refreshToken(): void {
    this.isRefreshing = true;

    this.authService.refreshTokens().subscribe({
      next: () => {
        this.isRefreshing = false;
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
      },
      error: (error) => {
        this.isRefreshing = false;

        if (error.status === 429) {
          const retryAfter = Math.max(1000, this.rateLimit.remainingMs() || 60_000);
          if (this.retryTimer) clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            this.checkAndRefreshToken();
          }, retryAfter);
          return;
        }

        // The refresh never answers COMPANY_TERMINATED by contract (offline marks must still
        // sync); if it ever does, it ends the service like any other request.
        const terminated = companyTerminatedDetails(error);
        if (terminated) {
          this.termination.end(terminated);
          return;
        }

        // Only invalid/revoked refresh credentials end the session.
        if (error.status === 400 || error.status === 401) {
          this.tokenService.clearTokens();
          this.authService.logout();
          this.router.navigate(['/login'], {
            queryParams: { reason: 'session_expired' },
          });
        }
      },
    });
  }
}
